import { BIOMES } from './biomes.js';
import { randomInt, randomFloat } from './random.js';
function idx(x, y, width) {
    return y * width + x;
}
export function generateMaze(seed, width, height, opts) {
    // Ensure odd dimensions for standard maze generation
    if (width % 2 === 0)
        width++;
    if (height % 2 === 0)
        height++;
    const tiles = new Array(width * height).fill('#');
    let rngCount = 0;
    const nextInt = (min, max) => {
        return randomInt(seed, rngCount++, 0, min, max); // Using salt=0, tick=rngCount
    };
    const nextFloat = () => {
        return randomFloat(seed, rngCount++, 0);
    };
    // Carving function (DFS)
    const carve = (x, y) => {
        tiles[idx(x, y, width)] = ' ';
        const dirs = [
            { dx: 0, dy: -2 },
            { dx: 0, dy: 2 },
            { dx: -2, dy: 0 },
            { dx: 2, dy: 0 }
        ];
        // Shuffle dirs
        for (let i = dirs.length - 1; i > 0; i--) {
            const j = nextInt(0, i + 1);
            const temp = dirs[i];
            dirs[i] = dirs[j];
            dirs[j] = temp;
        }
        for (const dir of dirs) {
            const nx = x + dir.dx;
            const ny = y + dir.dy;
            if (nx > 0 && nx < width - 1 && ny > 0 && ny < height - 1 && tiles[idx(nx, ny, width)] === '#') {
                tiles[idx(x + dir.dx / 2, y + dir.dy / 2, width)] = ' ';
                carve(nx, ny);
            }
        }
    };
    // Start carving from (1, 1)
    carve(1, 1);
    // Remove some random walls to create loops
    let loopsToCreate = Math.floor((width * height) * (opts?.loopPercentage ?? 0.05));
    while (loopsToCreate > 0) {
        const rx = nextInt(1, width - 1);
        const ry = nextInt(1, height - 1);
        if (tiles[idx(rx, ry, width)] === '#') {
            // Check if it separates two empty spaces
            let emptyNeighbors = 0;
            if (tiles[idx(rx - 1, ry, width)] === ' ')
                emptyNeighbors++;
            if (tiles[idx(rx + 1, ry, width)] === ' ')
                emptyNeighbors++;
            if (tiles[idx(rx, ry - 1, width)] === ' ')
                emptyNeighbors++;
            if (tiles[idx(rx, ry + 1, width)] === ' ')
                emptyNeighbors++;
            if (emptyNeighbors >= 2) {
                tiles[idx(rx, ry, width)] = ' ';
                loopsToCreate--;
            }
        }
    }
    // Find start and exit
    const start = { x: 1, y: 1 };
    const exit = { x: width - 2, y: height - 2 };
    // Make sure exit is reachable and open
    tiles[idx(exit.x, exit.y, width)] = 'E';
    // Populate dots and power pellets
    let dotCount = 0;
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            if (tiles[idx(x, y, width)] === ' ' && !(x === start.x && y === start.y)) {
                const pelletChance = opts?.density === 'low' ? 0.01 : opts?.density === 'medium' ? 0.02 : opts?.density === 'high' ? 0.04 : opts?.density === 'very_high' ? 0.06 : 0.02;
                if (nextFloat() < pelletChance) {
                    tiles[idx(x, y, width)] = 'o'; // power pellet
                }
                else if (opts?.hazards && opts.hazards.length > 0 && nextFloat() < 0.05) {
                    tiles[idx(x, y, width)] = 'T'; // trap
                }
                else if (opts?.hazards && opts.hazards.length > 0 && nextFloat() < 0.05) {
                }
                else {
                    tiles[idx(x, y, width)] = '.'; // dot
                    dotCount++;
                }
            }
        }
    }
    // Calculate difficulty metrics
    const pathData = analyzeReachability(tiles, width, height, start, exit);
    const isSolvable = pathData.solvable && pathData.reachableTiles === pathData.totalWalkableTiles;
    let difficulty = Math.floor(pathData.shortestPathToExit / 10) + Math.floor(dotCount / 50);
    const tags = [];
    if (pathData.deadEnds > (width * height) * 0.1)
        tags.push('labyrinth');
    else if (pathData.shortestPathToExit > (width + height))
        tags.push('long_path');
    else
        tags.push('standard');
    if (dotCount > 150)
        tags.push('dot_heavy');
    if (difficulty > 10)
        tags.push('hard');
    return { width, height, tiles, start, exit, difficulty, tags };
}
export function analyzeReachability(tiles, width, height, start, exit) {
    const q = [{ x: start.x, y: start.y, dist: 0 }];
    const visited = new Set();
    visited.add(idx(start.x, start.y, width));
    let shortestPathToExit = Infinity;
    let reachableTiles = 0;
    let totalWalkableTiles = 0;
    let deadEnds = 0;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (tiles[idx(x, y, width)] !== '#') {
                totalWalkableTiles++;
                let wallNeighbors = 0;
                if (tiles[idx(x - 1, y, width)] === '#')
                    wallNeighbors++;
                if (tiles[idx(x + 1, y, width)] === '#')
                    wallNeighbors++;
                if (tiles[idx(x, y - 1, width)] === '#')
                    wallNeighbors++;
                if (tiles[idx(x, y + 1, width)] === '#')
                    wallNeighbors++;
                if (wallNeighbors >= 3) {
                    deadEnds++;
                }
            }
        }
    }
    while (q.length > 0) {
        const { x, y, dist } = q.shift();
        reachableTiles++;
        if (x === exit.x && y === exit.y) {
            shortestPathToExit = dist;
        }
        const dirs = [{ dx: 0, dy: -1 }, { dx: 0, dy: 1 }, { dx: -1, dy: 0 }, { dx: 1, dy: 0 }];
        for (const d of dirs) {
            const nx = x + d.dx;
            const ny = y + d.dy;
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                const i = idx(nx, ny, width);
                if (tiles[i] !== '#' && !visited.has(i)) {
                    visited.add(i);
                    q.push({ x: nx, y: ny, dist: dist + 1 });
                }
            }
        }
    }
    return {
        solvable: shortestPathToExit !== Infinity,
        shortestPathToExit,
        reachableTiles,
        totalWalkableTiles,
        deadEnds
    };
}
export function generateBiomeMaze(seed, biomeId) {
    const config = BIOMES[biomeId];
    let rngCount = 0;
    const nextInt = (min, max) => {
        return randomInt(seed, rngCount++, 0, min, max);
    };
    const width = nextInt(config.seedConstraints.minSize, config.seedConstraints.maxSize);
    const height = nextInt(config.seedConstraints.minSize, config.seedConstraints.maxSize);
    return generateMaze(seed, width, height, {
        loopPercentage: config.seedConstraints.loopPercentage,
        density: config.seedConstraints.density,
        hazards: config.hazards
    });
}
